import React from "react";
import useSavedComponentsState from "../use-saved-components-state";
import useIsMounted from "../use-is-mounted";
import axios from "axios";
import BroadcastService from "../../../core/services/broadcast";

export interface PersonalPanelConfigurationsCachedData {
    showWhatsNewDialog?: boolean;
    showWelcomeToPanelTasksDialog?: boolean;
    showHappyBirthdayDialog?: boolean;
}

export type CachedDataKeys = (keyof PersonalPanelConfigurationsCachedData)[];

export const usePersonalPanelConfigurations = ({fetch = true} = {}) => {
    const [submitting, setSubmitting] = React.useState<CachedDataKeys>([]);
    const [loading, setLoading] = React.useState(true);
    const [cachedSettings, _setCachedSettings] = React.useState<PersonalPanelConfigurationsCachedData>();
    const isMounted = useIsMounted();
    const {highestVersion: getCachedPersonalPanelSettings, save: saveCachedPersonalPanelSettings} = useSavedComponentsState<PersonalPanelConfigurationsCachedData>({
        name: `personal-panel-settings`,
        version: '1.0.0'
    });

    /**
     * Sets the cached Personal settings and also saves them in the cache.
     */
    const setCachedPersonalPanelSettings = React.useCallback(async (updater, commit = true, properties: CachedDataKeys = []) => {
        const newState = await new Promise((resolve) => {
            _setCachedSettings((prevState) => {
                const newState = typeof updater === 'function' ? updater(prevState) : updater;
                resolve(newState);
                return newState;
            });
        });
        if (commit) {
            BroadcastService.postMessage(BroadcastService.channelNames.personalPanelSettings, newState);
            setSubmitting(prevState => [...prevState, ...properties]);
            void saveCachedPersonalPanelSettings(prevState => ({
                ...(prevState ?? {}),
                ...(newState ?? {}),
            })).finally(() => {
                setSubmitting(prevState => prevState.filter(e => properties?.includes(e)));
            });
        }
    }, [saveCachedPersonalPanelSettings])

    /**
     * Listens to changes in the Personal settings from other tabs/windows and updates the cached settings accordingly.
     */
    React.useEffect(() => {
        const {close} = BroadcastService.addEventListener(BroadcastService.channelNames.personalPanelSettings, 'message', (message) => {
            void setCachedPersonalPanelSettings(message, false);
        });
        return () => {
            close?.();
        }
    }, [setCachedPersonalPanelSettings]);

    /**
     * Fetches the cached Personal settings.
     */
    const fetchCachedPersonalPanelSettings = React.useCallback(async (cancelToken) => {
        setLoading(true);
        const cachedSettings = await getCachedPersonalPanelSettings(cancelToken);
        if (!isMounted()) {
            return;
        }
        setLoading(false);
        void setCachedPersonalPanelSettings(cachedSettings?.data, false);
    }, [getCachedPersonalPanelSettings, isMounted, setCachedPersonalPanelSettings]);

    /**
     * As soon as the component mounts:
     * - fetches the cached Personal settings.
     */
    React.useEffect(() => {
        if (!fetch)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchCachedPersonalPanelSettings(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [fetchCachedPersonalPanelSettings, fetch])


    const showWhatsNewDialog = React.useMemo(() => cachedSettings?.showWhatsNewDialog ?? true, [cachedSettings]);

    /**
     * Updates the "Show What's New Dialog" setting.
     * @type {(function(*): void)}
     */
    const setShowWhatsNewDialog = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>((value) => {
        void setCachedPersonalPanelSettings(prevState => ({
            ...prevState,
            showWhatsNewDialog: value,
        }), true, ['showWhatsNewDialog']);
    }, [setCachedPersonalPanelSettings]);

    const showWelcomeToPanelTasksDialog = React.useMemo(() => cachedSettings?.showWelcomeToPanelTasksDialog ?? true, [cachedSettings]);

    /**
     * Updates the "Show Welcome To Panel Tasks Dialog" setting.
     * @type {(function(*): void)}
     */
    const setShowWelcomeToPanelTasksDialog = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>((value) => {
        void setCachedPersonalPanelSettings(prevState => ({
            ...prevState,
            showWelcomeToPanelTasksDialog: value,
        }), true, ['showWelcomeToPanelTasksDialog']);
    }, [setCachedPersonalPanelSettings]);

    const showHappyBirthdayDialog = React.useMemo(() => cachedSettings?.showHappyBirthdayDialog ?? true, [cachedSettings]);

    /**
     * Updates the "Show Happy Birthday Dialog" setting.
     * @type {(function(*): void)}
     */
    const setShowHappyBirthdayDialog = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>((value) => {
        void setCachedPersonalPanelSettings(prevState => ({
            ...prevState,
            showHappyBirthdayDialog: value,
        }), true, ['showHappyBirthdayDialog']);
    }, [setCachedPersonalPanelSettings]);

    return ({
        loading,
        submitting,
        showWhatsNewDialog,
        setShowWhatsNewDialog,
        showWelcomeToPanelTasksDialog,
        setShowWelcomeToPanelTasksDialog,
        showHappyBirthdayDialog,
        setShowHappyBirthdayDialog,
    })
}
